import { ApiError, type ApiErrorDetail } from './api-error';

export interface RequestOptions {
  readonly signal?: AbortSignal;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly headers?: Readonly<Record<string, string>>;
}

/** Pagination counters the server reports alongside a list. */
export interface PageMeta {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

/** A list response with its pagination envelope preserved. */
export interface Page<T> {
  readonly items: readonly T[];
  readonly meta: PageMeta | null;
}

/**
 * The transport abstraction every feature depends on.
 *
 * Features receive this interface, never `fetch` directly, so tests can inject
 * a stub and the transport can be swapped without touching call sites.
 */
export interface HttpClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
  /**
   * A GET that keeps the `meta` envelope the plain `get` discards. Use it for
   * any list whose length the server decides — otherwise a truncated page is
   * indistinguishable from a complete one.
   */
  getPage<T>(path: string, options?: RequestOptions): Promise<Page<T>>;
}

interface ErrorPayload {
  message?: string;
  error?: { code?: string; message?: string; details?: ApiErrorDetail[] };
  details?: ApiErrorDetail[];
}

function buildUrl(baseUrl: string, path: string, query?: RequestOptions['query']): string {
  const url = `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: ErrorPayload = {};
  try {
    payload = (await response.json()) as ErrorPayload;
  } catch {
    // Non-JSON error body — fall back to the status text.
  }
  // The backend nests failures under `error`; the flat form is still read so a
  // proxy or gateway error surfaces its message instead of "Request failed".
  const message =
    payload.error?.message ?? payload.message ?? (response.statusText || 'Request failed');
  return ApiError.fromStatus(
    response.status,
    message,
    payload.error?.details ?? payload.details,
    payload.error?.code ?? null,
  );
}

interface Envelope {
  data?: unknown;
  meta?: unknown;
}

/** True for the `{ data, meta? }` wrapper the API returns on success. */
function isEnvelope(value: unknown): value is Envelope {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'data' in value;
}

function toPageMeta(meta: unknown): PageMeta | null {
  if (typeof meta !== 'object' || meta === null) return null;
  const m = meta as Record<string, unknown>;
  const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  const page = num(m.page);
  const pageSize = num(m.pageSize);
  const total = num(m.total);
  if (page === null || pageSize === null || total === null) return null;
  return {
    page,
    pageSize,
    total,
    totalPages: num(m.totalPages) ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 1),
  };
}

/** Parses the raw body, keeping the envelope intact for callers that need it. */
async function parseRaw(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (text === '') return undefined;
  return JSON.parse(text) as unknown;
}

/**
 * Unwraps the `{ data: ... }` envelope. A bare body is passed through so the
 * client keeps working against endpoints that do not wrap — the mock handlers
 * and any unmigrated route among them.
 */
async function parseBody<T>(response: Response): Promise<T> {
  const raw = await parseRaw(response);
  return (isEnvelope(raw) ? raw.data : raw) as T;
}

export interface FetchHttpClientOptions {
  readonly baseUrl: string;
  /** Injected so tests can drive requests without a global override. */
  readonly fetchImpl?: typeof fetch;
  /** Invoked on 401 so the app can end the session exactly once, centrally. */
  readonly onUnauthorized?: () => void;
  /**
   * Headers applied to every request — the auth header, in practice. Resolved
   * per call rather than captured, so a token that arrives after construction
   * is still picked up. Per-request headers win on conflict.
   *
   * May be async: acquiring an access token can require a network round trip
   * when the cached one has expired.
   */
  readonly defaultHeaders?: () =>
    Readonly<Record<string, string>> | Promise<Readonly<Record<string, string>>>;
}

export function createHttpClient(options: FetchHttpClientOptions): HttpClient {
  const { baseUrl, fetchImpl, onUnauthorized, defaultHeaders } = options;

  // Resolved per call, never captured at construction: binding `globalThis.fetch`
  // up front would freeze out anything that patches it afterwards — service
  // workers, tracing, and request mocking all install themselves later.
  const doFetch: typeof fetch = (input, init) => (fetchImpl ?? globalThis.fetch)(input, init);

  async function buildHeaders(
    body: unknown,
    reqOptions: RequestOptions,
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(await defaultHeaders?.()),
      ...reqOptions.headers,
    };
    if (body !== undefined && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  }

  function toRequestBody(body: unknown): BodyInit | null {
    if (body instanceof FormData) return body;
    return body === undefined ? null : JSON.stringify(body);
  }

  /** Performs the call and returns a response already known to be successful. */
  async function send(
    method: string,
    path: string,
    body: unknown,
    reqOptions: RequestOptions,
  ): Promise<Response> {
    // Resolved before the fetch so a failure to obtain a token surfaces as an
    // auth error rather than being flattened into "unable to reach the server".
    let headers: Record<string, string>;
    try {
      headers = await buildHeaders(body, reqOptions);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new ApiError('Could not authenticate this request.', {
        status: 401,
        code: 'unauthorized',
      });
    }

    let response: Response;
    try {
      response = await doFetch(buildUrl(baseUrl, path, reqOptions.query), {
        method,
        headers,
        // Authorisation travels in a header, not a cookie. Sending credentials
        // would force a CORS preflight the API is not configured to allow.
        credentials: 'omit',
        signal: reqOptions.signal,
        body: toRequestBody(body),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw ApiError.network();
    }

    if (!response.ok) {
      const error = await toApiError(response);
      if (error.code === 'unauthorized') onUnauthorized?.();
      throw error;
    }
    return response;
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    reqOptions: RequestOptions = {},
  ): Promise<T> {
    return parseBody<T>(await send(method, path, body, reqOptions));
  }

  async function getPage<T>(path: string, opts: RequestOptions = {}): Promise<Page<T>> {
    const raw = await parseRaw(await send('GET', path, undefined, opts));
    if (isEnvelope(raw)) {
      return {
        items: Array.isArray(raw.data) ? (raw.data as T[]) : [],
        meta: toPageMeta(raw.meta),
      };
    }
    // An unwrapped array (a mock handler, or a route yet to be migrated) is a
    // complete list by definition: no envelope means no further pages.
    return { items: Array.isArray(raw) ? (raw as T[]) : [], meta: null };
  }

  return {
    get: (path, opts) => request('GET', path, undefined, opts),
    post: (path, body, opts) => request('POST', path, body, opts),
    patch: (path, body, opts) => request('PATCH', path, body, opts),
    put: (path, body, opts) => request('PUT', path, body, opts),
    delete: (path, opts) => request('DELETE', path, undefined, opts),
    getPage,
  };
}
